import React, { useState, useEffect } from 'react';
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { User, Mail, Phone, Car, CalendarRange, CircleDollarSign, Receipt, AlertTriangle } from "lucide-react";
import { 
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage 
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { BookingCalendar } from "@/components/ui/booking-calendar";
import { Separator } from "@/components/ui/separator";
import { useCart, CartItem } from "@/contexts/CartContext";
import { useNavigate } from "react-router-dom";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { formatCurrency } from "@/lib/utils";
import { useSiteBookings } from "@/hooks/useSiteBookings";
import { checkSiteAvailability } from "@/services/campingSite/bookingService";
import { Alert, AlertDescription } from "@/components/ui/alert";

const formSchema = z.object({
  fullName: z.string().min(2, {
    message: "Full name must be at least 2 characters.",
  }),
  email: z.string().email({
    message: "Please enter a valid email address.",
  }),
  phone: z.string().min(10, {
    message: "Please enter a valid phone number.",
  }),
  vehicleLicensePlate: z.string().min(1, {
    message: "Vehicle license plate is required.",
  }),
  state: z.string().min(1, {
    message: "State is required.",
  }),
  comments: z.string().optional(),
  stayType: z.enum(["nightly", "weekly", "monthly"]),
  isPaved: z.boolean().default(true),
  dayUseOnly: z.boolean().default(false),
  rvDumpFee: z.boolean().default(false),
  termsAgreed: z.boolean().refine(val => val === true, {
    message: "You must accept the camping regulations.",
  }),
});

type BookingFormValues = z.infer<typeof formSchema>;

interface CampingBookingFormProps {
  siteId: string;
  siteName: string;
  startDate?: Date;
  endDate?: Date;
  price: number;
  totalDays?: number;
  onSuccess: () => void;
  editData?: CartItem | null;
}

const CampingBookingForm: React.FC<CampingBookingFormProps> = ({
  siteId,
  siteName,
  startDate: initialStartDate,
  endDate: initialEndDate,
  price,
  totalDays: initialTotalDays,
  onSuccess,
  editData
}) => {
  const { toast } = useToast();
  const { addItem, updateItem } = useCart();
  const navigate = useNavigate();
  
  const [startDate, setStartDate] = useState<Date | undefined>(
    editData?.startDate ? new Date(editData.startDate) : initialStartDate
  );
  const [endDate, setEndDate] = useState<Date | undefined>(
    editData?.endDate ? new Date(editData.endDate) : initialEndDate
  );
  const [calculatedDays, setCalculatedDays] = useState(initialTotalDays || 0);
  const [calculatedPrice, setCalculatedPrice] = useState(0);
  const [dateConflictError, setDateConflictError] = useState<string>("");
  
  // Get real-time booking data for this site
  const { bookings, isLoading: bookingsLoading } = useSiteBookings(siteId);
  
  // Check if we're in edit mode
  const isEditMode = !!editData;
  
  // Rate constants
  const pavedNightlyRate = 15;
  const pavedWeeklyRate = 75;
  const pavedMonthlyRate = 150;
  
  const unpavedNightlyRate = 10;
  const unpavedWeeklyRate = 50;
  const unpavedMonthlyRate = 125;
  
  const dayUseRate = 5;
  const rvDumpFee = 10;

  // 1. Define form with validation resolver
  const form = useForm<BookingFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      fullName: editData?.customerName || "",
      email: editData?.customerEmail || "",
      phone: editData?.phoneNumber || "",
      vehicleLicensePlate: editData?.licensePlate || "",
      state: editData?.state || "",
      comments: editData?.additionalDetails || "",
      stayType: (editData?.stayType as "nightly" | "weekly" | "monthly") || "nightly",
      isPaved: true,
      dayUseOnly: editData?.type === "day-use" || false,
      rvDumpFee: editData?.rvDumpFee || false,
      termsAgreed: true
    },
  });

  // Watch form values for price calculation
  const watchStayType = form.watch("stayType");
  const watchIsPaved = form.watch("isPaved");
  const watchDayUseOnly = form.watch("dayUseOnly");
  const watchRvDumpFee = form.watch("rvDumpFee");
  
  // Check for date conflicts when dates change
  useEffect(() => {
    const checkConflicts = async () => {
      if (!watchDayUseOnly && startDate && endDate && siteId) {
        try {
          const isAvailable = await checkSiteAvailability(siteId, startDate, endDate);
          if (!isAvailable) {
            setDateConflictError("Selected dates conflict with existing bookings. Please choose different dates.");
          } else {
            setDateConflictError("");
          }
        } catch (error) {
          console.error("Error checking availability:", error);
          setDateConflictError("Unable to verify date availability. Please try again.");
        }
      } else {
        setDateConflictError("");
      }
    };

    checkConflicts();
  }, [startDate, endDate, siteId, watchDayUseOnly]);
  
  // Calculate price based on selected options
  useEffect(() => {
    // If day use only, the price is simple
    if (watchDayUseOnly) {
      setCalculatedPrice(dayUseRate + (watchRvDumpFee ? rvDumpFee : 0));
      return;
    }
    
    if (startDate && endDate) {
      const days = Math.round((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
      setCalculatedDays(days);
      
      let basePrice = 0;
      
      if (watchStayType === "nightly") {
        basePrice = watchIsPaved ? pavedNightlyRate * days : unpavedNightlyRate * days;
      } else if (watchStayType === "weekly") {
        const weeks = Math.ceil(days / 7);
        basePrice = watchIsPaved ? pavedWeeklyRate * weeks : unpavedWeeklyRate * weeks;
      } else if (watchStayType === "monthly") {
        const months = Math.ceil(days / 30);
        basePrice = watchIsPaved ? pavedMonthlyRate * months : unpavedMonthlyRate * months;
      }
      
      setCalculatedPrice(basePrice + (watchRvDumpFee ? rvDumpFee : 0));
    } else {
      setCalculatedDays(0);
      setCalculatedPrice(watchRvDumpFee ? rvDumpFee : 0);
    }
  }, [startDate, endDate, watchStayType, watchIsPaved, watchDayUseOnly, watchRvDumpFee]);

  // 2. Handle form submission
  function onSubmit(values: BookingFormValues) {
    if (!values.dayUseOnly && (!startDate || !endDate)) {
      toast({
        title: "Date Selection Required",
        description: "Please select arrival and departure dates for overnight camping.",
        variant: "destructive"
      });
      return;
    }
    
    // Check for date conflicts before submitting
    if (!values.dayUseOnly && dateConflictError) {
      toast({
        title: "Date Conflict",
        description: dateConflictError,
        variant: "destructive"
      });
      return;
    }

    if (calculatedPrice <= 0) {
      toast({
        title: "Invalid Stay",
        description: "This stay comes to $0. Please check your dates and options.",
        variant: "destructive"
      });
      return;
    }
    
    
    // Generate unique ID for cart item or use existing ID if editing
    const uniqueCartItemId = editData?.id || `camp-${siteId}-${Date.now()}`;
    console.log(`${isEditMode ? "Updating" : "Creating"} camping booking with siteId: ${siteId}, cartItemId: ${uniqueCartItemId}`);
    
    const itemData = {
      id: uniqueCartItemId,
      siteId: siteId,
      type: values.dayUseOnly ? "day-use" as const : "camping" as const,
      permitCategory: "camping",
      permitName: values.dayUseOnly ? "Day Use" : `${values.isPaved ? 'Paved' : 'Unpaved'} - ${siteName}`,
      isAnnual: false,
      customerName: values.fullName,
      customerEmail: values.email,
      startDate: values.dayUseOnly ? new Date() : startDate as Date,
      endDate: values.dayUseOnly ? new Date() : endDate as Date,
      includesCamping: !values.dayUseOnly,
      basePrice: values.dayUseOnly ? 5 : (values.isPaved ? 15 : 10),
      totalPrice: calculatedPrice,
      phoneNumber: values.phone,
      licensePlate: values.vehicleLicensePlate,
      state: values.state,
      additionalDetails: values.comments || '',
      stayType: values.stayType,
      rvDumpFee: values.rvDumpFee
    };
    
    if (isEditMode) {
      // Update existing item
      updateItem(uniqueCartItemId, itemData);
      toast({
        title: "Booking Updated!",
        description: `Your booking for ${siteName} has been updated.`,
      });
    } else {
      // Add new item
      addItem(itemData as CartItem);
      toast({
        title: "Added to Cart!",
        description: `Your booking for ${siteName} has been added to cart.`,
      });
    }
    
    // Navigate to cart page after adding/updating item
    navigate("/cart");
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <div className="bg-tribal-primary/5 rounded-md p-4 mb-2">
          <div className="flex items-center justify-center mb-4">
            <img 
              src="/lovable-uploads/30b64ba5-3ca6-4187-a816-9405663d6025.png" 
              alt="Camping Rates"
              className="w-20 h-20 opacity-30"
            />
            <div className="ml-3">
              <h3 className="text-lg font-semibold">Shoshone-Paiute Tribes Camping</h3>
              <p className="text-sm text-tribal-neutral">Fish, Wildlife and Parks Dept. (208) 759-3246</p>
            </div>
          </div>
          
          <div className="text-sm border border-dashed border-tribal-neutral/50 p-3 rounded bg-white">
            <h4 className="font-medium mb-2 text-center">CAMPING RATES</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <h5 className="font-medium">PAVED (Main Campgrounds)</h5>
                <ul className="space-y-1">
                  <li>Nightly: $15.00 /vehicle</li>
                  <li>Weekly: $75.00</li>
                  <li>Monthly: $150.00</li>
                </ul>
              </div>
              <div>
                <h5 className="font-medium">UNPAVED</h5>
                <ul className="space-y-1">
                  <li>Nightly: $10.00 /vehicle</li>
                  <li>Weekly: $50.00</li>
                  <li>Monthly: $125.00</li>
                </ul>
              </div>
            </div>
            <div className="mt-2 text-center">
              <p>Day use fees are charged per vehicle: $5.00 / PER VEHICLE</p>
              <p>RV Dump Fee: $10.00</p>
            </div>
          </div>
          
          <div className="mt-3 text-sm">
            <ul className="space-y-1 text-tribal-neutral">
              <li>• Fees are charged per vehicle, per day.</li>
              <li>• Please display your receipt on driver's side of vehicle.</li>
              <li>• Make checks payable to "Shoshone-Paiute Tribes"</li>
            </ul>
          </div>
        </div>
        
        <div>
          <h3 className="text-sm font-medium mb-4 flex items-center">
            <Receipt className="h-4 w-4 mr-2 text-tribal-primary" />
            Select Permit Type
          </h3>
          
          <FormField
            control={form.control}
            name="dayUseOnly"
            render={({ field }) => (
              <FormItem className="flex flex-row items-start space-x-3 space-y-0 mb-4">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={(checked) => {
                      field.onChange(checked);
                      // Reset dates if switching to day use
                      if (checked) {
                        setStartDate(undefined);
                        setEndDate(undefined);
                      }
                    }}
                  />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel>
                    Day Use Only ($5.00 per vehicle)
                  </FormLabel>
                </div>
              </FormItem>
            )}
          />
          
          {!form.watch("dayUseOnly") && (
            <>
              <FormField
                control={form.control}
                name="isPaved"
                render={({ field }) => (
                  <FormItem className="space-y-3 mb-4">
                    <FormLabel>Campsite Type</FormLabel>
                    <FormControl>
                      <RadioGroup
                        onValueChange={(value) => field.onChange(value === "paved")}
                        defaultValue={field.value ? "paved" : "unpaved"}
                        className="flex flex-col space-y-1"
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="paved" id="paved" />
                          <FormLabel htmlFor="paved" className="font-normal">
                            Paved (Main Campgrounds)
                          </FormLabel>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="unpaved" id="unpaved" />
                          <FormLabel htmlFor="unpaved" className="font-normal">
                            Unpaved
                          </FormLabel>
                        </div>
                      </RadioGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="stayType"
                render={({ field }) => (
                  <FormItem className="space-y-3 mb-4">
                    <FormLabel>Stay Duration</FormLabel>
                    <FormControl>
                      <RadioGroup
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                        className="flex flex-col space-y-1"
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="nightly" id="nightly" />
                          <FormLabel htmlFor="nightly" className="font-normal">
                            Nightly ({form.watch("isPaved") ? "$15" : "$10"} per night)
                          </FormLabel>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="weekly" id="weekly" />
                          <FormLabel htmlFor="weekly" className="font-normal">
                            Weekly ({form.watch("isPaved") ? "$75" : "$50"} per week)
                          </FormLabel>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="monthly" id="monthly" />
                          <FormLabel htmlFor="monthly" className="font-normal">
                            Monthly ({form.watch("isPaved") ? "$150" : "$125"} per month)
                          </FormLabel>
                        </div>
                      </RadioGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </>
          )}
          
          <FormField
            control={form.control}
            name="rvDumpFee"
            render={({ field }) => (
              <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel>
                    Add RV Dump Fee ($10.00)
                  </FormLabel>
                </div>
              </FormItem>
            )}
          />
        </div>
        
        {!form.watch("dayUseOnly") && (
          <div>
            <h3 className="text-sm font-medium mb-2 flex items-center">
              <CalendarRange className="h-4 w-4 mr-2 text-tribal-primary" />
              Select Dates
            </h3>
            
            {/* Date Conflict Warning */}
            {dateConflictError && (
              <Alert variant="destructive" className="mb-4">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  {dateConflictError}
                </AlertDescription>
              </Alert>
            )}
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <BookingCalendar
                date={startDate}
                setDate={setStartDate}
                label="Arrival Date"
                placeholder="Select arrival date"
                bookings={bookings}
                disabled={bookingsLoading}
                mode="checkin"
              />
              <BookingCalendar
                date={endDate}
                setDate={setEndDate}
                label="Departure Date"
                placeholder="Select departure date"
                bookings={bookings}
                disabled={bookingsLoading || !startDate}
                mode="checkout"
                defaultMonth={startDate}
                highlightDate={startDate}
                rangeEnd={endDate}
              />
            </div>
          </div>
        )}
        
        <Separator />
        
        <div>
          <h3 className="text-sm font-medium mb-4 flex items-center">
            <User className="h-4 w-4 mr-2 text-tribal-primary" />
            Contact Information
          </h3>
          
          <div className="grid grid-cols-1 gap-4">
            <FormField
              control={form.control}
              name="fullName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Full Name</FormLabel>
                  <FormControl>
                    <Input placeholder="John Doe" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    Email
                  </FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="your@email.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-2">
                    <Phone className="h-4 w-4" />
                    Phone Number
                  </FormLabel>
                  <FormControl>
                    <Input type="tel" placeholder="(555) 123-4567" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
        
        <div>
          <h3 className="text-sm font-medium mb-4 flex items-center">
            <Car className="h-4 w-4 mr-2 text-tribal-primary" />
            Vehicle Information
          </h3>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="vehicleLicensePlate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Vehicle License Plate #</FormLabel>
                  <FormControl>
                    <Input placeholder="ABC123" {...field} className="uppercase" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="state"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>State</FormLabel>
                  <FormControl>
                    <Input placeholder="NV" {...field} maxLength={2} className="uppercase" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
        
        <FormField
          control={form.control}
          name="comments"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Comments</FormLabel>
              <FormControl>
                <Input placeholder="Additional comments or special requests" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <div className="bg-tribal-primary/5 p-4 rounded-md">
          <h3 className="text-sm font-medium mb-2 flex items-center">
            <CircleDollarSign className="h-4 w-4 mr-2 text-tribal-primary" />
            Pricing Summary
          </h3>
          
          <div className="space-y-1 text-sm">
            {form.watch("dayUseOnly") ? (
              <div className="flex justify-between">
                <span>Day use fee:</span>
                <span>$5.00</span>
              </div>
            ) : (
              <>
                <div className="flex justify-between">
                  <span>Campsite type:</span>
                  <span>{form.watch("isPaved") ? "Paved" : "Unpaved"}</span>
                </div>
                <div className="flex justify-between">
                  <span>Stay type:</span>
                  <span className="capitalize">{watchStayType}</span>
                </div>
                {watchStayType === "nightly" && calculatedDays > 0 && (
                  <div className="flex justify-between">
                    <span>Number of nights:</span>
                    <span>{calculatedDays}</span>
                  </div>
                )}
                {watchStayType === "weekly" && calculatedDays > 0 && (
                  <div className="flex justify-between">
                    <span>Number of weeks:</span>
                    <span>{Math.ceil(calculatedDays / 7)}</span>
                  </div>
                )}
                {watchStayType === "monthly" && calculatedDays > 0 && (
                  <div className="flex justify-between">
                    <span>Number of months:</span>
                    <span>{Math.ceil(calculatedDays / 30)}</span>
                  </div>
                )}
              </>
            )}
            {watchRvDumpFee && (
              <div className="flex justify-between">
                <span>RV Dump Fee:</span>
                <span>$10.00</span>
              </div>
            )}
            <Separator className="my-2" />
            <div className="flex justify-between font-medium">
              <span>Total amount:</span>
              <span>{formatCurrency(calculatedPrice)}</span>
            </div>
          </div>
        </div>
        
        <div>
          <FormField
            control={form.control}
            name="termsAgreed"
            render={({ field }) => (
              <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel className="text-sm">
                    I agree to the Tribal camping regulations and fees.
                  </FormLabel>
                  <FormMessage />
                </div>
              </FormItem>
            )}
          />
        </div>
        
        <div className="flex flex-col sm:flex-row gap-3 pt-2">
          <Button 
            type="submit" 
            className="flex-1 bg-tribal-primary hover:bg-tribal-primary/90"
            disabled={!!dateConflictError && !form.watch("dayUseOnly")}
          >
            Add to Cart
          </Button>
          <Button 
            type="button" 
            variant="outline"
            className="flex-1"
            onClick={onSuccess}
          >
            Cancel
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default CampingBookingForm;
